import type { Diagnostic } from "./validateFlow";
import type { FlowDocument, FlowNode } from "./types";

/**
 * Mirrors Go's own per-node-type required-port table
 * (services/api/internal/flow/graph.go's `requiredPorts`) — the first piece
 * of docs/plans/06's "Semantic validation — implement in Go; mirror
 * diagnostics in web" that has actually been mirrored, rather than left as
 * "the schema-diagnostics panel is the only feedback" (every earlier
 * designer slice's own stated limitation). Kept as a single switch, one
 * case per node type, so a port-table change on the Go side has one obvious
 * place to update here too — there is no schema-driven way to derive this
 * table generically, since it depends on each node type's own execution
 * semantics, not just its config shape.
 *
 * `gather` and `branch` are the two dynamic cases: their own ports depend on
 * the node's current config (`valid_keys`, `cases`), not a fixed list, so
 * this reads that config the same defensive way nodeConfigForm.ts already
 * does — a malformed or mid-edit config degrades to the fixed ports only,
 * never throws.
 */
export function requiredPorts(node: FlowNode): string[] {
  switch (node.type) {
    case "start":
      return ["next"];
    case "play":
    case "tts":
    case "consent":
    case "http":
      return ["next", "error"];
    case "tag":
      return ["next"];
    case "hangup":
      return [];
    case "amd_gate":
      return ["human", "machine", "notsure"];
    case "repeat":
      return ["repeat", "exceeded"];
    case "transfer":
      // A successful transfer is a terminal handoff with no exit port of
      // its own; only the failure path returns to this IVR (graph.go's own
      // comment on the same rule).
      return ["failure"];
    case "gather": {
      const config = node.config && typeof node.config === "object" ? (node.config as Record<string, unknown>) : {};
      const validKeys = Array.isArray(config.valid_keys)
        ? config.valid_keys.filter((key): key is string => typeof key === "string")
        : [];
      return [...validKeys.map((key) => `key:${key}`), "timeout", "invalid"];
    }
    case "branch": {
      const config = node.config && typeof node.config === "object" ? (node.config as Record<string, unknown>) : {};
      const cases = Array.isArray(config.cases) ? config.cases : [];
      const ids = cases
        .map((item) => (item && typeof item === "object" ? (item as Record<string, unknown>).id : undefined))
        .filter((id): id is string => typeof id === "string");
      return [...ids.map((id) => `case:${id}`), "default"];
    }
    default:
      return [];
  }
}

/** Which of a node's own required ports have no outgoing edge for them. */
export function missingPorts(document: FlowDocument, nodeId: string): string[] {
  const node = document.nodes.find((candidate) => candidate.id === nodeId);
  if (!node) {
    return [];
  }
  const wired = new Set(document.edges.filter((edge) => edge.source === nodeId).map((edge) => edge.port));
  return requiredPorts(node).filter((port) => !wired.has(port));
}

/**
 * The semantic-layer equivalent of Go's `checkPorts`/`CodeMissingPort` —
 * same layer name, same code, same message shape — so this shows up
 * alongside the existing schema diagnostics as one more real check, not a
 * separate, differently-labeled feature.
 */
export function checkMissingPorts(document: FlowDocument): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  for (const node of document.nodes) {
    const required = [...requiredPorts(node)].sort();
    for (const port of missingPorts(document, node.id)) {
      diagnostics.push({
        layer: "semantic",
        code: "missing_required_port",
        path: `/nodes/${node.id}`,
        detail: `${node.type} node is missing required port "${port}" (required: ${required.join(", ")})`,
      });
    }
  }
  return diagnostics;
}
